<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.log-data {
    font-size: smaller;
    vertical-align: top;
    text-align: left;

    .command-line {
        font-family: monospace;
        margin-left: 20px;
    }
}

.log-blob {
    white-space: pre-wrap;
    font-family: monospace;
    word-break: break-word;
}

.button-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    button {
        margin: 10px 20px;

        .fa-arrow-left {
            margin-right: 15px;
        }

        .fa-chevron-left.second {
            margin-right: 15px;
        }

        .fa-arrow-right {
            margin-left: 15px;
        }

        .fa-chevron-right.first {
            margin-left: 15px;
        }
    }
}

.comm-log-table {
    table-layout: fixed;
    margin-bottom: 0;
    color: $theme-dark-grey;

    th,
    td {
        border: 1px solid $theme-light-grey;
    }

    thead th:nth-child(1) {
        width: 80px;
    }

    thead th:nth-child(2) {
        width: 85px;
    }

    thead th:nth-child(3) {
        width: 85px;
    }

    thead th:nth-child(4) {
        width: 15%;
    }

    .user-block {
        display: flex;
        flex-direction: column;
        word-break: break-word;

        span:first-child {
            font-weight: 700;
        }
    }
}

.entry-table {
    border: 1px solid $theme-light-grey;
    border-left: none;
    border-right: none;
}
</style>

<template>
    <Card color="blue" fullWidth noBodyPad>
        <template #header>{{ $t('auditLog.base') }}</template>
        <template #body>
            <div class="button-row">
                <Button
                    outline
                    :disabled="previousButtonDisabled"
                    @click="goToBeginning"
                >
                    <i class="fa fa-chevron-left"></i>
                    <i class="fa fa-chevron-left second"></i>
                    {{ $t('start') }}
                </Button>
                <div>
                    <Button
                        outline
                        :disabled="previousButtonDisabled"
                        @click="getPreviousPage"
                    >
                        <i class="fa fa-arrow-left"></i>
                        {{ $t('prev') }}
                    </Button>
                    <Button outline :disabled="onLastPage" @click="getNewPage">
                        {{ $t('next') }}
                        <i class="fa fa-arrow-right"></i>
                    </Button>
                </div>
                <Button outline :disabled="onLastPage" @click="goToEnd">
                    {{ $t('end') }}
                    <i class="fa fa-chevron-right first"></i>
                    <i class="fa fa-chevron-right"></i>
                </Button>
            </div>
            <table
                class="table table-bordered table-hover table-sm comm-log-table"
            >
                <thead>
                    <tr role="row">
                        <th>{{ $t('time') }}</th>
                        <th>{{ $t('user') }}</th>
                        <th>{{ $t('action') }}</th>
                        <th>{{ $t('data') }}</th>
                    </tr>
                </thead>
                <tr v-for="log in logs" :key="log.id" class="log-data">
                    <td>{{ log.timestamp }}</td>
                    <td>
                        <div class="user-block">
                            <span>{{ log.user }}</span>
                            <span>{{ log.user_email }}</span>
                        </div>
                    </td>
                    <td
                        v-if="
                            log.action === Actions.Update ||
                            log.action === Actions.Delete ||
                            log.action === Actions.Create
                        "
                    >
                        {{ getReusedActionString(log) }}
                    </td>
                    <td v-else>
                        {{
                            $t('auditLog.' + log.action, {
                                entity: log.target_name,
                                interpolation: { escapeValue: false },
                            })
                        }}
                    </td>
                    <td>
                        <!-- The contents of this section are different depending on log type -->
                        <span v-if="log.action === Actions.BadPassword">
                            {{
                                $t('auditLog.badPasswordInfo', {
                                    email: log.data.email,
                                })
                            }}
                        </span>
                        <div v-else-if="log.action === Actions.UnitCommand">
                            <span
                                v-for="line in getCommandLines(log.data)"
                                :key="line"
                                class="command-line"
                            >
                                {{ line }}
                            </span>
                        </div>
                        <NewDetails
                            v-else-if="log.action === Actions.Create"
                            :data="log.data.after"
                        />
                        <Diff v-else :data="log.data" />
                    </td>
                </tr>
            </table>
            <div class="button-row">
                <Button
                    outline
                    :disabled="previousButtonDisabled"
                    @click="goToBeginning"
                >
                    <i class="fa fa-chevron-left"></i>
                    <i class="fa fa-chevron-left second"></i>
                    {{ $t('start') }}
                </Button>
                <div>
                    <Button
                        outline
                        :disabled="previousButtonDisabled"
                        @click="getPreviousPage"
                    >
                        <i class="fa fa-arrow-left"></i>
                        {{ $t('prev') }}
                    </Button>
                    <Button outline :disabled="onLastPage" @click="getNewPage">
                        {{ $t('next') }}
                        <i class="fa fa-arrow-right"></i>
                    </Button>
                </div>
                <Button outline :disabled="onLastPage" @click="goToEnd">
                    {{ $t('end') }}
                    <i class="fa fa-chevron-right first"></i>
                    <i class="fa fa-chevron-right"></i>
                </Button>
            </div>
        </template>
    </Card>
</template>

<script>
import * as client from '../../../api/client';
import { changeMonitor } from '../../../api/ChangeMonitor';
import Diff from './Diff.vue';
import NewDetails from './NewDetails.vue';
import { Actions } from './Actions';

export function formatAuditLogs(rawLogs, timeFormatter) {
    const elements = [];

    for (const log of rawLogs) {
        const e = {};
        e.timestamp = timeFormatter.format(new Date(log.timestamp));
        e.user = log.user_name;
        e.user_email = log.user_email;
        e.action = log.action;
        e.target_table = log.target_table;
        e.target_name = log.target_name;
        // e.data = JSON.stringify(log.data, null, " ");
        e.data = log.data;
        e.id = log.id;
        elements.push(e);
    }

    return elements;
}

export default {
    data() {
        return {
            rawLogs: [],
            pageSize: 20, // Customizable, can be changed as desired later
            firstIndex: -1,
            prevIndex: -1,
            nextIndex: -1,
            onLastPage: false,
        };
    },

    computed: {
        Actions() {
            return Actions;
        },

        logs() {
            return formatAuditLogs(
                this.rawLogs,
                this.$store.getters.timeFormatter,
            );
        },

        previousButtonDisabled() {
            return this.prevIndex >= this.firstIndex;
        },
    },

    created: async function () {
        const data = await client.get(`/logs/audit?limit=${this.pageSize}`);
        this.rawLogs = data.payload;
        this.firstIndex = data.payload[0].id;
        this.prevIndex = this.firstIndex;
        this.nextIndex = data.payload[data.payload.length - 1].id;
        if (data.payload.length < this.pageSize) {
            this.onLastPage = true;
        }

        changeMonitor.addNotifiedUser(this.handleChanges);
    },

    methods: {
        handleChanges(changeRecord) {
            if (changeRecord.changeType === 'AUDIT_LOG') {
                // If we are still on the first page, we can shift the new entry to the front
                if (this.rawLogs) {
                    if (this.previousButtonDisabled) {
                        this.rawLogs.unshift(changeRecord.data);
                        this.prevIndex = changeRecord.data.id;
                        this.firstIndex = changeRecord.data.id;
                    }
                } else {
                    this.rawLogs = [changeRecord.data];
                    this.prevIndex = changeRecord.data.id;
                    this.firstIndex = changeRecord.data.id;
                }
            }
        },

        // Returns lines of text related to sending commands to a unit
        getCommandLines(data) {
            if (data.after) {
                const lines = [];
                for (const property of Object.keys(data.after)) {
                    lines.push(
                        this.$t('auditLog.UNIT_COMMAND_DETAILS', {
                            property,
                            value: data.after[property],
                        }),
                    );
                }
                return lines;
            } else {
                return ['--'];
            }
        },

        getReusedActionString(log) {
            // Remove pluralization for the target table and combine translation strings
            const targetTable = log.target_table.slice(
                0,
                log.target_table.length - 1,
            );
            return this.$t(`auditLog.${log.action}`, {
                entity: log.target_name,
                table: this.$t(targetTable),
                interpolation: { escapeValue: false },
            });
        },

        async getPreviousPage() {
            const data = await client.get(
                `/logs/audit?limit=${this.pageSize}&prev=${this.prevIndex}`,
            );
            this.rawLogs = data.payload;
            // We'll always have a previous page if we make this call, if we don't the button was disabled
            this.prevIndex = parseInt(data.payload[0].id);
            this.nextIndex = data.payload[data.payload.length - 1].id;
            // It's impossible to go backwards from another page and be on the last page
            this.onLastPage = false;

            this.scrollToTop();
        },

        async getNewPage() {
            const data = await client.get(
                `/logs/audit?limit=${this.pageSize}&next=${this.nextIndex}`,
            );
            this.rawLogs = data.payload;
            // We may or may not get any results, set previous index first to allow us to go back
            this.prevIndex = this.nextIndex - 1;
            if (data.payload && data.payload.length > 0) {
                this.nextIndex = data.payload[data.payload.length - 1].id;
            }

            // If we didn't get a full page or nothing at all, we're done, flag it
            if (
                !data.payload.length > 0 ||
                data.payload.length < this.pageSize
            ) {
                this.onLastPage = true;
            } else {
                this.onLastPage = false;
            }

            this.scrollToTop();
        },

        async goToBeginning() {
            const data = await client.get(`/logs/audit?limit=${this.pageSize}`);
            this.rawLogs = data.payload;
            this.prevIndex = this.firstIndex;
            this.nextIndex = data.payload[data.payload.length - 1].id;
            this.onLastPage = false;

            this.scrollToTop();
        },

        async goToEnd() {
            const data = await client.get(
                `/logs/audit?limit=${this.pageSize}&prev=0`,
            );
            this.rawLogs = data.payload;
            // If the end button was enabled, there must be at least one full page of results
            this.prevIndex = this.pageSize;
            this.nextIndex = 1;
            this.onLastPage = true;

            this.scrollToTop();
        },

        scrollToTop() {
            document.getElementById('app-main').scrollTo(0, 0);
        },
    },

    beforeDestroy: function () {
        changeMonitor.removeNotifiedUser(this.handleChanges);
    },

    components: {
        Diff: Diff,
        NewDetails: NewDetails,
    },
};
</script>
